import { RealtimeChannel } from '@supabase/supabase-js';

/**
 * Extended RealtimeChannel interface with internal properties
 * Used for subscription duplicate detection and channel management
 */
export interface ExtendedRealtimeChannel extends RealtimeChannel {
  /**
   * Internal subscription state (Supabase internal)
   */
  _subscription?: {
    subscriptions?: { length: number };
  };
  
  /**
   * Channel bindings for event listeners
   */
  bindings?: {
    /**
     * All bindings regardless of type
     */
    all?: unknown[];
    
    /**
     * Postgres changes specific bindings
     */
    postgres_changes?: Array<{
      event: string;
      schema: string;
      table: string;
      filter?: string;
    }>;
  };
  
  /**
   * Channel topic/name
   */
  topic?: string;
}

/**
 * Type guard to check if a channel is an ExtendedRealtimeChannel
 */
export function isExtendedRealtimeChannel(
  channel: RealtimeChannel
): channel is ExtendedRealtimeChannel {
  return channel !== null && typeof channel === 'object';
}

/**
 * Get subscription count from channel
 */
export function getChannelSubscriptionCount(channel: ExtendedRealtimeChannel): number {
  return channel._subscription?.subscriptions?.length || 0;
}

/**
 * Check if channel has bindings
 */
export function channelHasBindings(channel: ExtendedRealtimeChannel): boolean {
  return (channel.bindings?.all?.length || 0) > 0;
}

/**
 * Check if channel has specific postgres changes listener
 */
export function channelHasPostgresListener(
  channel: ExtendedRealtimeChannel,
  config: {
    event: string;
    schema: string;
    table: string;
    filter?: string;
  }
): boolean {
  const listeners = channel.bindings?.postgres_changes || [];
  return listeners.some((listener) => {
    return (
      listener.event === config.event &&
      listener.schema === config.schema &&
      listener.table === config.table &&
      listener.filter === config.filter
    );
  });
}